// Copyright (c) StrangeBee 2024

import { SharingEventList } from '@Organisms/Shares/EntitySharingList/EntitySharingList';
import {
    ObservableController,
    ObservableCreateReturn,
    ActionOutput,
    ObservableInput,
    ObservableOutput,
    FilterInput,
    SharedOrgOutput,
    DescribeOutput,
} from 'thehive-sdk';
import { ObservableBulkEditValues } from '@Organisms/Observable/ObservableBulkEdit/ObservableBulkEditForm';
import { handleNotificationError } from '@Helpers/notification';
import client from '@Client';

export interface ObservableEventList {
    onGetLastReport: (analyzerId: string, observableId: string) => Promise<ActionOutput>;
    onGetReport: (jobId: string) => Promise<ActionOutput>;
    shares: SharingEventList;
    onCreate: (
        observable: ObservableInput,
        caseId: string,
        callback?: () => void,
        alertId?: string,
    ) => Promise<ObservableOutput[] | ObservableOutput | ObservableCreateReturn | void>;
    onAlertCreate: (
        observable: ObservableInput,
        alertId?: string,
        callback?: () => void,
    ) => Promise<ObservableOutput[] | ObservableOutput | ObservableCreateReturn | void>;
    onEdit: (observable: Partial<ObservableInput>, observableId: string) => Promise<void>;
    getDownload: (params: Record<string, string>) => string;
    onBulkEdit: (observables: ObservableOutput[], fieldsEdited: ObservableBulkEditValues) => Promise<void>;
}

interface ObservableEventListParams {
    locale: (label: string) => string;
    controller: ObservableController;
    onSuccess: (count?: boolean, message?: string) => void;
    onError: (label?: string) => void;
    describeData?: DescribeOutput;
}

export const computeEventList = ({
    controller,
    locale,
    onError,
    onSuccess,
    describeData,
}: ObservableEventListParams): ObservableEventList => ({
    onCreate: (observable: ObservableInput, caseId: string, callback) =>
        controller
            .create(observable, caseId)
            .then((ret) => {
                if ('failure' in ret) {
                    ret.failure.map(({ type, message, object }) =>
                        handleNotificationError({ type, message: `${message}: ${object.data || ''} ` }),
                    );
                    if (ret.success.length) {
                        if (callback) {
                            callback();
                        } else {
                            onSuccess(true, locale('notification.observable.create'));
                        }
                    }
                } else {
                    if (callback) {
                        callback();
                    } else {
                        onSuccess(true, locale('notification.observable.create'));
                    }
                }
                return ret;
            })
            .catch(onError),
    onAlertCreate: (observable: ObservableInput, alertId, callback) =>
        controller
            .alertCreate(observable, alertId)
            .then((ret) => {
                if ('failure' in ret) {
                    ret.failure.map(({ type, message, object }) =>
                        handleNotificationError({ type, message: `${message}: ${object.data || ''} ` }),
                    );
                    if (ret.success.length) {
                        if (callback) {
                            callback();
                        } else {
                            onSuccess(true, locale('notification.observable.create'));
                        }
                    }
                } else {
                    if (callback) {
                        callback();
                    } else {
                        onSuccess(true, locale('notification.observable.create'));
                    }
                }
                return ret;
            })
            .catch(onError),
    onEdit: (observable: Partial<ObservableInput>, observableId: string): Promise<void> => {
        if (Object.keys(observable).includes('sightedAt') && !observable.sightedAt) {
            observable.sightedAt = null;
        }
        return controller
            .edit(observable, observableId)
            .then(() => onSuccess(false, locale('notification.observable.edit')))
            .catch(onError);
    },
    getDownload: (params: Record<string, string>): string =>
        client.apiUrl + controller.getUrlDownloadAttachment(params),

    onBulkEdit: (observables: ObservableOutput[], fieldsEdited: ObservableBulkEditValues): Promise<void> => {
        return controller
            .multiEdit(
                observables.map((o) => o._id),
                fieldsEdited,
            )
            .then(() => onSuccess(false, locale('notification.observable.edit')))
            .catch(onError);
    },
    onGetLastReport: (analyzerId, observableId): Promise<ActionOutput> =>
        controller.one({
            selector: {
                head: [{ name: 'getObservable', idOrName: observableId }, { name: 'jobs' }],
                name: `get-observable-report-${observableId}`,
                extraData: ['report'],
            },
            filters: [
                {
                    field: 'analyzerDefinition',
                    values: analyzerId,
                    operator: 'any',
                    type: 'string',
                    describeAttribute: describeData?.observable.attributes.find(
                        ({ name }) => name === 'analyzerDefinition',
                    ) as any,
                },
            ],
            sort: ['-startDate'],
            paginationOptions: { page: 0, itemsPerPage: 1 },
        }),
    onGetReport: (jobId): Promise<ActionOutput> =>
        controller.one({
            selector: {
                head: [{ name: 'getJob', idOrName: jobId }],
                name: `get-observable-report-${jobId}`,
                extraData: ['report'],
            },
            paginationOptions: { page: 0, itemsPerPage: 1 },
        }),
    shares: {
        onList: (observableId: string, filters?: FilterInput[]): Promise<SharedOrgOutput[]> =>
            controller.list({
                selector: {
                    head: [{ name: 'getObservable', idOrName: observableId }, { name: 'shares' }],
                    initialFilters: filters,
                    name: 'get-observable-sharings',
                },
            }),
        onCaseShareList: (caseId: string, filters?: FilterInput[]): Promise<SharedOrgOutput[]> =>
            controller.list({
                selector: {
                    head: [{ name: 'getCase', idOrName: caseId }, { name: 'shares' }],
                    initialFilters: filters,
                    name: 'get-case-sharing',
                },
            }),
        onAdd: (shares, observableId: string): void => {
            controller
                .addShare(shares as string[], observableId)
                .then(() => onSuccess?.(true, locale('notification.task.sharing.add')))
                .catch(onError);
        },
        onDelete: (organisations, observableId): void => {
            controller
                .deleteShare(organisations, observableId)
                .then(() => onSuccess?.(true, locale('notification.observable.sharing.remove')))
                .catch(onError);
        },
    },
});
